import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HelpCircle, Loader2, SearchX, TrendingUp, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { exportExcel } from '@/lib/exporters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMBIENTES = [
  { id: '', label: 'Todos os painéis' },
  { id: 'dono', label: 'Dono' },
  { id: 'master', label: 'Master' },
  { id: 'afiliado', label: 'Afiliado' },
  { id: 'parceiro', label: 'Parceiro' },
  { id: 'publica', label: 'Site público' },
];

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const AdminHelpSearchesTab = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [ambiente, setAmbiente] = useState('');
  const [data, setData] = useState({
    total_searches: 0, unique_terms: 0, no_results_total: 0,
    terms: [], gaps: [], words: [], by_screen: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/help/searches`, {
        params: { days, ...(ambiente ? { ambiente } : {}) },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setData(res.data);
    } catch { toast.error('Erro ao carregar as buscas da ajuda'); }
    finally { setLoading(false); }
  }, [days, ambiente]);

  useEffect(() => { load(); }, [load]);

  const exportTerms = () => {
    if (!data.terms.length) return toast.error('Nada para exportar');
    exportExcel(data.terms.map(t => ({
      'Palavra buscada': t.term,
      'Vezes': t.total,
      'Sem resposta': t.no_results,
      'Painel': t.ambientes.join(' · '),
      'Telas': t.telas.join(' · '),
      'Última busca': fmtWhen(t.last_at),
    })), `duvidas-buscadas-${new Date().toISOString().slice(0, 10)}`, 'Buscas');
  };

  const maxWord = data.words[0]?.total || 1;

  return (
    <div className="space-y-4" data-testid="admin-help-searches-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-total-searches">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">
            <HelpCircle className="w-3.5 h-3.5" /> Buscas na ajuda
          </div>
          <p className="text-2xl font-bold text-[#0A2647]">{data.total_searches}</p>
          <p className="text-[11px] text-slate-400">{data.unique_terms} palavra(s) diferente(s)</p>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4" data-testid="stat-no-results">
          <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-wider font-bold mb-1">
            <SearchX className="w-3.5 h-3.5" /> Não acharam resposta
          </div>
          <p className="text-2xl font-bold text-red-600">{data.no_results_total}</p>
          <p className="text-[11px] text-slate-400">{data.gaps.length} palavra(s) sem conteúdo</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-top-screens">
          <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">
            <TrendingUp className="w-3.5 h-3.5" /> Telas que mais geram dúvida
          </div>
          <ol className="text-xs text-slate-600 space-y-0.5">
            {data.by_screen.length
              ? data.by_screen.slice(0, 3).map((s, i) => (
                <li key={s.tela}><span className="font-semibold">{i + 1}.</span> {s.tela} — {s.total}</li>
              ))
              : <li className="text-slate-400">Sem buscas ainda</li>}
          </ol>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-days">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={365}>Último ano</option>
        </select>
        <select value={ambiente} onChange={e => setAmbiente(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-ambiente">
          {AMBIENTES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        <Button onClick={exportTerms} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2 ml-auto"
          data-testid="export-searches-btn">
          <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center bg-white rounded-xl border border-slate-200">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </div>
      ) : !data.total_searches ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center" data-testid="searches-empty">
          <p className="text-sm text-slate-500">Ninguém buscou nada na ajuda nesse período.</p>
          <p className="text-xs text-slate-400 mt-1">Assim que alguém digitar no campo de busca da Ajuda, as palavras aparecem aqui.</p>
        </div>
      ) : (
        <>
          {data.gaps.length > 0 && (
            <div className="bg-white rounded-xl border border-red-200 overflow-hidden" data-testid="gaps-card">
              <div className="px-4 py-3 bg-red-50 border-b border-red-100">
                <p className="text-sm font-bold text-red-800">O que falta escrever</p>
                <p className="text-xs text-red-700 mt-0.5">
                  Essas palavras foram buscadas e a ajuda não tinha resposta. É aqui que vale escrever primeiro.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="gaps-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2.5">Palavra buscada</th>
                      <th className="text-left px-3 py-2.5">Sem resposta</th>
                      <th className="text-left px-3 py-2.5">Onde buscaram</th>
                      <th className="text-left px-3 py-2.5">Última vez</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.gaps.map(t => (
                      <tr key={t.term} className="hover:bg-slate-50" data-testid={`gap-row-${t.term}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-800">"{t.term}"</td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            {t.no_results}x
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{t.telas.join(' · ') || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{fmtWhen(t.last_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-bold text-[#0A2647]">Mais buscadas</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="terms-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2.5">Palavra</th>
                      <th className="text-left px-3 py-2.5">Vezes</th>
                      <th className="text-left px-3 py-2.5">Telas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.terms.slice(0, 25).map(t => (
                      <tr key={t.term} className="hover:bg-slate-50" data-testid={`term-row-${t.term}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-800">"{t.term}"</td>
                        <td className="px-3 py-2.5 text-slate-600">{t.total}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{t.telas.join(' · ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-bold text-[#0A2647] mb-3">Palavras mais repetidas</p>
              <div className="space-y-2" data-testid="words-cloud">
                {data.words.map(w => (
                  <div key={w.word} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-28 truncate">{w.word}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#C5A059] rounded-full"
                        style={{ width: `${Math.max(6, (w.total / maxWord) * 100)}%` }} />
                    </div>
                    <span className="text-xs text-slate-400 w-6 text-right">{w.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminHelpSearchesTab;
